// Synchronous localStorage snapshot of the ACTIVE entity's branding.
//
// On a hard refresh the branding query hasn't resolved yet, so the sidebar and
// theme would paint the default blue + uKnowva logo for a frame before the real
// branding arrives (a "flash of default branding"). Reading this snapshot on the
// first render — localStorage is synchronous — lets the app paint the correct
// logo/colour immediately. We keep only the active entity (one key, so one logo
// at most) to stay well under the storage quota.

/** The branding fields that paint the app chrome and theme. */
const CHROME_KEYS = [
  'primary_color', 'primary_button_color', 'logo_b64', 'logo_icon_b64',
  'sidebar_text_color', 'header_color', 'text_color', 'link_color',
  'secondary_button_color', 'mobile_theme_color', 'font_family', 'font_size',
] as const;

export type CachedBranding = {
  primary_color: string | null;
  primary_button_color: string | null;
  logo_b64: string | null;
  logo_icon_b64?: string | null;
  sidebar_text_color?: string | null;
  header_color?: string | null;
  text_color?: string | null;
  link_color?: string | null;
  secondary_button_color?: string | null;
  mobile_theme_color?: string | null;
  font_family?: string | null;
  font_size?: number | null;
};

const KEY = 'ukn_active_branding';

/** Snapshot for `entityId`, or null if we have none (or it's for another entity). */
export function readCachedBranding(entityId: string | null): CachedBranding | null {
  if (!entityId) return null;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const o = JSON.parse(raw) as { entityId: string; branding: CachedBranding };
    return o && o.entityId === entityId ? o.branding : null;
  } catch {
    return null;
  }
}

/** Persist the active entity's branding so the next load paints it instantly. */
export function writeCachedBranding(entityId: string | null, b: Partial<CachedBranding> | null | undefined): void {
  if (!entityId) return;
  try {
    localStorage.setItem(KEY, JSON.stringify({
      entityId,
      branding: Object.fromEntries(CHROME_KEYS.map((k) => [k, b?.[k] ?? null])),
    }));
  } catch {
    /* quota exceeded or storage disabled — the flash just isn't suppressed */
  }
}
